import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import {
  DocumentTextIcon,
  PlusIcon,
  ShareIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAnalytics } from '../hooks/useAnalytics';

const QuoteStatus = ({ status }) => {
  const getStatusColor = () => {
    switch (status.toLowerCase()) {
      case 'accepted':
        return THEME.success;
      case 'pending':
        return THEME.warning;
      case 'declined':
        return THEME.error;
      default:
        return THEME.subtext;
    }
  };

  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: `${getStatusColor()}15`,
        color: getStatusColor(),
      }}
    >
      {status}
    </span>
  );
};

export default function Quotes() {
  const { logEvent } = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Search and filter states
  const [searchField, setSearchField] = useState('quote_number');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [selectedStatus, setSelectedStatus] = useState('');

  const searchFields = [
    { value: 'quote_number', label: 'Quote Number' },
    { value: 'client_name', label: 'Client Name' },
    { value: 'status', label: 'Status' },
    { value: 'quote_date', label: 'Quote Date' },
    { value: 'expiry_date', label: 'Expiry Date' },
    { value: 'total', label: 'Amount' },
  ];

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    filterQuotes();
  }, [quotes, searchField, searchQuery, dateRange, selectedStatus]);

  const filterQuotes = () => {
    let filtered = [...quotes];

    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(quote => {
        if (searchField === 'quote_number') {
          return quote.quote_number.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchField === 'client_name') {
          return quote.clients?.client_name.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchField === 'status') {
          return quote.status.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchField === 'total') {
          return quote.total.toString().includes(searchQuery);
        }
        return true;
      });
    }

    // Apply date range filter
    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(quote => {
        const quoteDate = new Date(searchField === 'expiry_date' ? quote.expiry_date : quote.quote_date);
        
        if (dateRange.startDate && dateRange.endDate) {
          return quoteDate >= new Date(dateRange.startDate) && quoteDate <= new Date(dateRange.endDate);
        } else if (dateRange.startDate) {
          return quoteDate >= new Date(dateRange.startDate);
        } else if (dateRange.endDate) {
          return quoteDate <= new Date(dateRange.endDate);
        }
        return true;
      });
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(quote => quote.status === selectedStatus);
    }

    setFilteredQuotes(filtered);
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchQuery('');
    setDateRange({ startDate: '', endDate: '' });
  };

  const handleSearchQueryChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e) => {
    logEvent('Quotes', 'Status Change', `Quote status changed to ${e.target.value}`);
    setSelectedStatus(e.target.value);
  };

  const clearFilters = () => {
    setSearchField('quote_number');
    setSearchQuery('');
    setDateRange({ startDate: '', endDate: '' });
    setSelectedStatus('');
    setShowFilters(false);
  };

  const fetchQuotes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('quotes')
        .select(`
          *,
          clients (
            client_id,
            client_name,
            client_email
          ),
          public_quotes (
            id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;

    try {
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('quote_id', quoteId);
      logEvent('Quotes', 'Delete Quote', `Quote ID: ${quoteId}`);
      if (error) throw error;
      await fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      setError('Failed to delete quote');
    }
  };

  const handleShare = async (quote) => {
    try {
      // Check if public quote already exists
      if (quote.public_quotes?.[0]?.id) {
        // Copy existing link to clipboard
        
        const shareableLink = `${window.location.origin}/quote/${quote.public_quotes[0].id}`;
        await navigator.clipboard.writeText(shareableLink);
        alert('Link copied to clipboard!');
        logEvent('Quotes', 'Share Quote', `Quote ID: ${quote.quote_id}`);
        return;
      }

      // Create new public quote
      const { data, error } = await supabase
        .from('public_quotes')
        .insert({
          quote_id: quote.quote_id,
          user_id: quote.user_id,
          customization_options: {},
        })
        .select()
        .single();

      if (error) throw error;

      // Copy new link to clipboard
      const shareableLink = `${window.location.origin}/quote/${data.id}`;
      await navigator.clipboard.writeText(shareableLink);
      alert('Link copied to clipboard!');
      logEvent('Quotes', 'Share Quote', `Quote ID: ${quote.quote_id}`);
      await fetchQuotes();
    } catch (error) {
      console.error('Error sharing quote:', error);
      setError('Failed to share quote');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>Quotes</h1>
          <p style={{ color: THEME.subtext }}>Manage your quotes and proposals</p>
        </div>
        <Link
          to="/quotes/new"
          className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
          style={{ 
            background: THEME.primary,
            boxShadow: shadows.md,
          }}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Quote
        </Link>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 flex items-center space-x-2">
            <select
              value={searchField}
              onChange={handleSearchFieldChange}
              className="rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            >
              {searchFields.map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
            
            {searchField === 'quote_date' || searchField === 'expiry_date' ? (
              <div className="flex-1 flex items-center space-x-2">
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateRangeChange}
                  className="flex-1 rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder="Start Date"
                />
                <span style={{ color: THEME.text }}>to</span>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateRangeChange}
                  className="flex-1 rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder="End Date"
                />
              </div>
            ) : (
              <div className="flex-1 relative">
                <input
                  type={searchField === 'total' ? 'number' : 'text'}
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  className="w-full rounded-lg pl-10 border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder={`Search by ${searchFields.find(f => f.value === searchField)?.label}`}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-2 rounded-lg transition-colors duration-200"
            style={{ 
              backgroundColor: showFilters ? `${THEME.primary}15` : 'transparent',
              color: showFilters ? THEME.primary : THEME.text 
            }}
          >
            <FunnelIcon className="w-5 h-5" />
          </button>

          {(searchQuery || dateRange.startDate || dateRange.endDate || selectedStatus) && (
            <button
              onClick={clearFilters}
              className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors duration-200"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {showFilters && (
          <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: `${THEME.primary}08` }}>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={handleStatusChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Accepted">Accepted</option>
                <option value="Declined">Declined</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div 
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
        >
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl" style={{ boxShadow: shadows.md }}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quote
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredQuotes.map((quote) => (
                <tr key={quote.quote_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" style={{ color: THEME.primary }} />
                      <span style={{ color: THEME.text }}>#{quote.quote_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: THEME.text }}>
                      {quote.clients?.client_name}
                    </div>
                    <div className="text-sm" style={{ color: THEME.subtext }}>
                      {quote.clients?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium" style={{ color: THEME.text }}>
                      ${quote.total?.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <QuoteStatus status={quote.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: THEME.text }}>
                      {new Date(quote.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        onClick={() => handleShare(quote)}
                        className="text-gray-400 hover:text-gray-500"
                        title="Share Quote"
                      >
                        <ShareIcon className="h-5 w-5" />
                      </button>
                      <Link
                        to={`/quotes/${quote.quote_id}`}
                        className="text-gray-400 hover:text-gray-500"
                        title="Edit Quote"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(quote.quote_id)}
                        className="text-gray-400 hover:text-red-500"
                        title="Delete Quote"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      <Link
                        to={`/quotes/${quote.quote_id}`}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredQuotes.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center" style={{ color: THEME.subtext }}>
                    {quotes.length === 0 ? 
                      'No quotes found. Create your first quote to get started!' :
                      'No quotes match your search criteria.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 